import React, { useId } from 'react';

import { cn, Card, CardHeader, CardContent } from 'shadcn-ui';
import { ChevronDown } from 'lucide-react';
import useCollapsibleSectionState from './useCollapsibleSectionState';
import { BorderedBoxProps } from './BorderedBox';

export default function ShadcnBorderedBox(props: BorderedBoxProps) {
  const contentId = useId();
  const { isCollapsed, toggleSection } = useCollapsibleSectionState(props.pageContext);

  const collapsed = props.collapsible && props.sectionId
    ? isCollapsed(props.sectionId)
    : false;

  const handleToggle = () => {
    if (props.collapsible && props.sectionId) {
      toggleSection(props.sectionId);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (props.collapsible && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      handleToggle();
    }
  };

  return (
    <Card
      className={cn('mb-3', props.className)}
      data-testid={props.testId}
    >
      <CardHeader
        className={cn(
          'flex flex-row items-center gap-2',
          !collapsed && 'border-b border-border',
          props.collapsible && 'cursor-pointer select-none',
          props.collapsible && !collapsed && 'hover:text-blue-500',
          props.collapsible && 'focus-visible:outline-2 focus-visible:outline-blue-500 focus-visible:outline-offset-2',
          collapsed && 'border-b-0',
        )}
        onClick={props.collapsible ? handleToggle : undefined}
        onKeyDown={props.collapsible ? handleKeyDown : undefined}
        role={props.collapsible ? 'button' : undefined}
        tabIndex={props.collapsible ? 0 : undefined}
        aria-expanded={props.collapsible ? !collapsed : undefined}
        aria-controls={props.collapsible ? contentId : undefined}
      >
        <h5 className="!my-0 flex-grow">
          {props.title}
          {props.count !== undefined && (
            <span className="text-app-body text-gray-500 font-normal ml-1">
              ({props.count})
            </span>
          )}
        </h5>
        {props.headerRight && (
          <span
            className={cn(
              'shrink-0',
              props.collapsible && 'cursor-default',
              collapsed && 'hidden',
            )}
            onClick={props.collapsible ? (e) => e.stopPropagation() : undefined}
          >
            {props.headerRight}
          </span>
        )}
        {props.collapsible && (
          <ChevronDown
            className={cn(
              'size-[1.2rem] shrink-0 text-gray-400 transition-transform duration-200 ml-4',
              collapsed && '-rotate-90',
            )}
          />
        )}
      </CardHeader>
      <CardContent
        id={contentId}
        className={cn(
          'overflow-y-auto overflow-x-hidden transition-all duration-200',
          collapsed ? 'max-h-0 !py-0 opacity-0' : 'max-h-[100rem] opacity-100',
        )}
      >
        {props.children}
      </CardContent>
    </Card>
  );
}
